import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUp, Box, Check, ChevronDown, ChevronRight, FileArchive, FileCode2, Gauge, Globe, Image as ImageIcon, Loader2, Lock, Mic, Paperclip, Plus, RotateCcw, Shapes, Slash, Sparkles, Square, X,
} from "lucide-react";
import { toast } from "sonner";
import { MODEL_INFO, errorText, vai } from "@/lib/vai";

// Zone de saisie de Vanta AI (comme claude.ai) :
// - menu + en bas à gauche : fichiers et photos (Ctrl+U), commandes « / », outils, artefacts ;
// - pièces jointes au-dessus du texte (code, projet .zip, photo), aussi par glisser-déposer ou coller ;
// - à droite : choix du modèle (Pulse, Vector · Vanta Code), dictée, envoyer / arrêter.
// Entrée envoie, Maj + Entrée va à la ligne.

export const TOOL_ICONS = { search: Globe, image: ImageIcon, model3d: Box };
const TOOL_HINTS = {
  search: "Cherche sur internet quand c'est utile",
  image: "Photos, illustrations, logos et affiches",
  model3d: "Modèles 3D Blender, aperçu et fichier .glb",
};
const ACCEPT = ".lua,.luau,.js,.mjs,.cjs,.jsx,.ts,.tsx,.json,.py,.html,.htm,.css,.scss,.md,.txt,.cfg,.ini,.yml,.yaml,.xml,.sql,.sh,.php,.java,.cs,.cpp,.c,.h,.go,.rs,.toml,.csv,.log,.vue,.svelte,.zip,image/png,image/jpeg,image/webp,image/gif";

// Commandes « / » : un raccourci qui prépare la demande.
export const SLASH_COMMANDS = [
  { cmd: "zip", label: "Projet complet en .zip", prompt: "Crée un projet complet à télécharger en .zip : " },
  { cmd: "copier", label: "Code à copier-coller", prompt: "Donne-moi le code à copier-coller, fichier par fichier : " },
  { cmd: "site", label: "Site web avec aperçu", prompt: "Crée un site web complet (une page HTML avec aperçu) : " },
  { cmd: "fivem", label: "Ressource FiveM", prompt: "Crée une ressource FiveM complète en .zip (fxmanifest.lua, client, server, config) : " },
  { cmd: "roblox", label: "Script Roblox (Luau)", prompt: "Écris un script Roblox en Luau, avec où le placer dans Studio : " },
  { cmd: "bot", label: "Bot Discord", prompt: "Crée un bot Discord (discord.js v14) complet en .zip : " },
  { cmd: "corriger", label: "Corriger mon code", prompt: "Corrige ce code, explique chaque erreur, puis donne la version corrigée : " },
  { cmd: "image", label: "Créer une image", prompt: "Fais-moi une image de ", tool: "image" },
  { cmd: "3d", label: "Modèle 3D (Pro)", prompt: "Crée un modèle 3D de ", tool: "model3d" },
];

function useOutside(ref, onOutside, active) {
  useEffect(() => {
    if (!active) return undefined;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onOutside(); };
    const esc = (e) => { if (e.key === "Escape") onOutside(); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", handler); document.removeEventListener("keydown", esc); };
  }, [ref, onOutside, active]);
}

// Menus du bas à droite : sur téléphone ils prennent la largeur de la zone de saisie (sinon ils
// sortent de l'écran à gauche), sur ordinateur ils s'ouvrent au-dessus de leur bouton.
const POP_POS = "bottom-14 inset-x-2 sm:inset-x-auto sm:bottom-10 sm:right-0";
const MENU = "vai-pop absolute z-30 rounded-xl border border-white/10 bg-[#17171f] shadow-2xl shadow-black/50 p-1.5";
const ITEM = "w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left text-sm hover:bg-white/[0.06] disabled:opacity-40 disabled:hover:bg-transparent";

// Outils : « Auto », Vanta AI choisit tout seul (recherche, image, 3D quand la demande en a
// l'air) ; « Manuel », l'outil choisi sert à chaque message, et rien d'autre.
function PlusMenu({ me, tools, toolMode, onToolMode, onSelect, onUpgrade, onFiles, onSlash, onArtifacts }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false), open);
  const close = (fn) => () => { setOpen(false); fn(); };
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
        className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-colors ${open ? "bg-white/10 border-white/20 text-white" : "border-white/10 text-white/60 hover:text-white hover:bg-white/5"}`}
        aria-label="Ajouter" title="Ajouter des fichiers, outils…" data-testid="vai-tools-button">
        <Plus className={`w-4 h-4 transition-transform ${open ? "rotate-45" : ""}`} />
      </button>
      {open && (
        <div className={`${MENU} bottom-10 left-0 w-72`} data-testid="vai-tools-menu">
          <button type="button" className={ITEM} onClick={close(onFiles)} data-testid="vai-add-files">
            <Paperclip className="w-4 h-4 text-white/60" /><span className="flex-1">Ajouter des fichiers ou des photos</span>
            <kbd className="text-[10px] text-white/35">Ctrl+U</kbd>
          </button>
          <button type="button" className={ITEM} onClick={close(onSlash)} data-testid="vai-add-slash">
            <Slash className="w-4 h-4 text-white/60" /><span className="flex-1">Commandes slash</span><kbd className="text-[10px] text-white/35">/</kbd>
          </button>
          <button type="button" className={ITEM} onClick={close(onArtifacts)} data-testid="vai-add-artifacts">
            <Shapes className="w-4 h-4 text-white/60" /><span className="flex-1">Mes artefacts</span>
          </button>
          <div className="my-1.5 border-t border-white/[0.07]" />
          <div className="flex items-center justify-between px-2.5 pt-0.5 pb-1.5">
            <p className="text-[11px] uppercase tracking-wide text-white/35">Outils</p>
            <div className="flex rounded-lg bg-white/[0.06] p-0.5 text-xs" role="radiogroup" aria-label="Choix des outils">
              {[["auto", "Auto"], ["manual", "Manuel"]].map(([id, label]) => (
                <button key={id} type="button" role="radio" aria-checked={toolMode === id} onClick={() => onToolMode(id)}
                  className={`px-2.5 py-1 rounded-md transition-colors ${toolMode === id ? "bg-white text-black font-medium" : "text-white/60 hover:text-white"}`}
                  data-testid={`vai-tool-mode-${id}`}>{label}</button>
              ))}
            </div>
          </div>
          {toolMode === "auto" ? (
            <p className="px-2.5 pb-1.5 text-[11px] leading-relaxed text-white/45" data-testid="vai-tool-auto-hint">
              Vanta AI choisit tout seul : recherche sur internet, image ou 3D quand ta demande en a besoin.
            </p>
          ) : me.tools.map((t) => {
            const Icon = TOOL_ICONS[t.id] || Globe;
            const on = tools.includes(t.id);
            const locked = !t.allowed;
            return (
              <button key={t.id} type="button" role="radio" aria-checked={on} disabled={!locked && !t.available}
                onClick={() => (locked ? close(onUpgrade)() : onSelect(t.id))} className={ITEM} data-testid={`vai-tool-${t.id}`}>
                <Icon className="w-4 h-4 text-white/60 shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block">{t.name}</span>
                  <span className="block text-[11px] text-white/40">{!t.available && !locked ? "Pas encore disponible" : TOOL_HINTS[t.id]}</span>
                </span>
                {locked ? <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md border border-violet-400/30 bg-violet-500/10 text-violet-200"><Lock className="w-3 h-3" />Pro</span>
                  : <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${on ? "border-sky-300 bg-sky-400/20" : "border-white/25"}`}>
                      {on && <span className="w-2 h-2 rounded-full bg-sky-300" />}
                    </span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ModelPicker({ me, value, onChange, onUpgrade }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false), open);
  const current = me.models.find((m) => m.id === value) || me.models[0];
  const online = me.engine.online && current?.available;
  return (
    <div className="sm:relative" ref={ref}>
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1.5 px-2 h-8 rounded-lg text-sm text-white/75 hover:text-white hover:bg-white/5" data-testid="vai-model-picker">
        <span className={`w-1.5 h-1.5 rounded-full ${online ? "bg-emerald-400" : "bg-amber-400"}`} aria-hidden="true" />
        {current?.name || MODEL_INFO[value]?.name}
        <ChevronDown className="w-3.5 h-3.5 opacity-60" />
      </button>
      {open && (
        <div className={`${MENU} ${POP_POS} sm:w-80`} data-testid="vai-model-menu">
          {me.models.map((m) => (
            <button key={m.id} type="button" disabled={m.allowed && !m.available}
              onClick={() => { setOpen(false); if (!m.allowed) onUpgrade(); else onChange(m.id); }}
              className={`${ITEM} items-start`} data-testid={`vai-model-${m.id}`}>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2 font-medium">{m.name}{m.badge && <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-gradient-to-r from-blue-500/25 to-violet-500/25 border border-violet-400/30 text-violet-100">{m.badge}</span>}</span>
                <span className="block text-[11px] text-white/45 mt-0.5">{m.allowed && !m.available ? "Hors ligne pour le moment" : m.tagline}</span>
              </span>
              {!m.allowed ? <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md border border-violet-400/30 bg-violet-500/10 text-violet-200"><Lock className="w-3 h-3" />Pro</span>
                : value === m.id && <Check className="w-4 h-4 mt-0.5 text-sky-300" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Niveau de réflexion (Vector seulement, offres payantes) : curseur à 5 crans, comme sur
// claude.ai. Pulse a un réglage fixe : le bouton est grisé et propose Vector.
export const EFFORT_ORDER = ["minimal", "moyen", "eleve", "tres_eleve", "ultra"];

export function EffortPicker({ me, model, effort, onEffort, onModel, onUpgrade }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false), open);
  const efforts = me.efforts || [];
  const vector = me.models.find((m) => m.id === "vector");
  const index = Math.max(0, EFFORT_ORDER.indexOf(effort));
  const current = efforts.find((e) => e.id === EFFORT_ORDER[index]) || { name: "Moyen" };
  const ultra = EFFORT_ORDER[index] === "ultra";
  if (model !== "vector") {
    return (
      <button type="button" onClick={() => (vector?.allowed ? onModel("vector") : onUpgrade())}
        className="w-8 h-8 rounded-lg flex items-center justify-center text-white/25 hover:text-white/50 hover:bg-white/5"
        title={vector?.allowed ? "Réflexion fixe avec Pulse : passe à Vector pour la régler" : "Réflexion réglable avec Vector · Vanta Code (Pro et Max)"}
        aria-label="Niveau de réflexion (Vector seulement)" data-testid="vai-effort-locked">
        <Gauge className="w-4 h-4" />
      </button>
    );
  }
  return (
    <div className="sm:relative" ref={ref}>
      <button type="button" onClick={() => setOpen(!open)} className={`flex items-center gap-1.5 px-2 h-8 rounded-lg text-xs hover:bg-white/5 ${ultra ? "text-violet-200" : "text-white/70 hover:text-white"}`}
        aria-label={`Niveau de réflexion : ${current.name}`} data-testid="vai-effort-button">
        {ultra ? <Sparkles className="w-4 h-4" /> : <Gauge className="w-4 h-4" />}<span className="hidden sm:inline">{current.name}</span>
      </button>
      {open && (
        <div className={`${MENU} ${POP_POS} sm:w-72 p-3`} data-testid="vai-effort-menu">
          <div className="flex items-start gap-2">
            <Gauge className="w-4 h-4 mt-1 text-white/40" />
            <div className="flex-1 text-center">
              <p className={`text-[15px] font-semibold ${ultra ? "text-violet-300" : "text-sky-400"}`} data-testid="vai-effort-name">{current.name}</p>
              {ultra ? <p className="text-[11px] text-white/60 mt-0.5">Consomme le quota plus rapidement</p>
                : <button type="button" onClick={() => setOpen(false)} className="inline-flex items-center text-[11px] text-white/60 mt-0.5 hover:text-white">Vector · Vanta Code<ChevronRight className="w-3 h-3" /></button>}
            </div>
            <button type="button" onClick={() => onEffort("moyen")} className="p-1 rounded-md text-white/40 hover:text-white hover:bg-white/10" aria-label="Revenir au niveau Moyen" title="Revenir à Moyen"><RotateCcw className="w-3.5 h-3.5" /></button>
          </div>
          <div className="relative mt-3 h-8">
            <div className={`absolute inset-0 rounded-full overflow-hidden ${ultra ? "bg-gradient-to-r from-indigo-900 via-violet-600 to-fuchsia-400" : "bg-white/[0.07]"}`}>
              {!ultra && <div className="absolute inset-y-0 left-0 rounded-full bg-blue-600 transition-all" style={{ width: `calc(${(index / 4) * 100}% * 0.86 + 32px)` }} />}
              {ultra && <div className="absolute inset-0 opacity-60 bg-[radial-gradient(circle,rgba(255,255,255,0.7)_1px,transparent_1.5px)] [background-size:14px_9px]" />}
            </div>
            <div className="absolute inset-0 flex items-center justify-between px-4 pointer-events-none">
              {EFFORT_ORDER.map((id, i) => (
                <span key={id} className={`rounded-full transition-all ${i === index ? "w-6 h-6 bg-white shadow-lg -mx-2" : `w-1.5 h-1.5 ${i < index || ultra ? "bg-white/70" : "bg-white/25"}`}`} />
              ))}
            </div>
            <input type="range" min={0} max={4} step={1} value={index} onChange={(e) => onEffort(EFFORT_ORDER[Number(e.target.value)])}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" aria-label="Niveau de réflexion" data-testid="vai-effort-slider" />
          </div>
          <p className="mt-2 text-[11px] text-white/40 text-center">
            {current.thinks ? "Vector réfléchit d'abord (réflexion visible), puis répond. " : ""}{current.cost} unité{current.cost > 1 ? "s" : ""} par message
          </p>
        </div>
      )}
    </div>
  );
}

// Dictée : le texte s'écrit en direct, mot par mot, pendant qu'on parle (reconnaissance vocale
// du navigateur : Chrome, Edge, Safari, l'appli Android). Sinon (Firefox…), le micro enregistre
// et le site transcrit à la fin. Rien n'est envoyé tant que la personne ne valide pas.
const SpeechRecognition = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
const ANDROID = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");

// Langue de la dictée : celle choisie dans les réglages, sinon celle du téléphone ou du navigateur.
function dictationLang(me) {
  const chosen = me.settings?.language;
  return chosen && chosen !== "auto" ? chosen : navigator.language || "fr-FR";
}

function joinText(...parts) {
  return parts.map((p) => p.trim()).filter(Boolean).join(" ");
}

function pickMime() {
  const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  return types.find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
}

// Niveau du micro (0 à 1) lu en direct sur le son, pour la bande qui montre qu'on est entendu.
function streamMeter(stream) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  const ctx = new Ctx();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  ctx.createMediaStreamSource(stream).connect(analyser);
  const buf = new Uint8Array(analyser.fftSize);
  return {
    read() {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i += 1) { const x = (buf[i] - 128) / 128; sum += x * x; }
      return Math.min(1, Math.sqrt(sum / buf.length) * 6);
    },
    close() { ctx.close().catch(() => {}); },
  };
}

// Bande de son façon claude.ai : les barres défilent de droite à gauche, hautes quand on
// parle, réduites à des points dans le silence.
const WAVE_BARS = 22;
function VoiceWave({ meter }) {
  const [levels, setLevels] = useState(() => Array(WAVE_BARS).fill(0));
  useEffect(() => {
    const id = setInterval(() => {
      const level = meter.current?.read() || 0;
      setLevels((l) => [...l.slice(1), level]);
    }, 70);
    return () => clearInterval(id);
  }, [meter]);
  return (
    <span className="w-[60px] sm:w-[88px] h-5 flex items-center justify-end gap-[2px] overflow-hidden" aria-hidden="true" data-testid="vai-dictate-wave">
      {levels.map((l, i) => (
        <span key={i} className="w-[2px] shrink-0 rounded-full bg-white transition-[height] duration-75"
          style={{ height: `${Math.max(2, Math.round(l * 18))}px`, opacity: l > 0.04 ? 0.95 : 0.45 }} />
      ))}
    </span>
  );
}

// La reconnaissance vocale du navigateur passe par Google (Chrome) ou Apple (Safari) : certains
// navigateurs d'ordinateur la bloquent (Brave, Opera, Chromium…, erreur « network »). On passe
// alors, et pour la suite de la visite, à la dictée par morceaux.
let browserDictationBroken = false;

function DictateButton({ value, onLive, onText, lang, disabled }) {
  const [state, setState] = useState("idle"); // idle | recording
  const [seconds, setSeconds] = useState(0);
  const chunked = useRef(null); // dictée par morceaux : { stream, recorder, active, cancelled, base, parts, pending }
  const live = useRef(null); // dictée en direct : { recognition, active, base, done }
  const meter = useRef(null); // { read(), close() } : niveau du micro pour la bande de son
  const closeMeter = () => { meter.current?.close(); meter.current = null; };
  const liveOut = useRef(onLive);
  liveOut.current = onLive;
  useEffect(() => {
    if (state !== "recording") return undefined;
    const start = Date.now();
    const id = setInterval(() => {
      const s = Math.floor((Date.now() - start) / 1000);
      setSeconds(s);
      if (s >= 240) stop(); // 4 minutes au plus
    }, 250);
    return () => clearInterval(id);
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps
  const cancel = () => {
    const l = live.current; if (l) { l.active = false; l.cancelled = true; l.recognition.abort(); }
    const c = chunked.current; if (c) { c.active = false; c.cancelled = true; c.finish(); }
  };
  useEffect(() => () => { cancel(); closeMeter(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Message envoyé (ou zone vidée) pendant la dictée : on coupe sans réécrire dans la zone.
  useEffect(() => {
    const session = live.current || chunked.current;
    if (session && (disabled || (!value && session.written))) cancel();
  }, [disabled, value]); // eslint-disable-line react-hooks/exhaustive-deps

  const stop = () => {
    if (live.current) { live.current.active = false; live.current.recognition.stop(); }
    const c = chunked.current;
    if (c) { c.active = false; if (c.recorder?.state === "recording") c.recorder.stop(); else c.finish(); }
  };

  // Chaque phrase reconnue est ajoutée dès qu'elle est sûre ; les mots en cours de
  // reconnaissance s'affichent aussi et se corrigent tout seuls. Le navigateur coupe
  // l'écoute après un silence : on la relance tant que la personne n'a pas arrêté.
  const startLive = () => {
    const recognition = new SpeechRecognition();
    const session = { recognition, active: true, base: value || "", done: "" };
    recognition.lang = lang;
    recognition.interimResults = true;
    recognition.continuous = !ANDROID; // Chrome Android répète les phrases en mode continu
    recognition.maxAlternatives = 1;
    let heard = "";
    // Sur téléphone, le micro est réservé à la reconnaissance : la bande suit ce qu'elle
    // entend. Sur ordinateur, on lit en plus le vrai niveau du micro.
    let sound = false, lastWords = 0;
    recognition.onsoundstart = () => { sound = true; };
    recognition.onsoundend = () => { sound = false; };
    meter.current = { read: () => (sound || Date.now() - lastWords < 450 ? 0.25 + Math.random() * 0.75 : 0), close() {} };
    if (!/Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "") && navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        const m = live.current === session && streamMeter(stream);
        if (!m) { stream.getTracks().forEach((t) => t.stop()); return; }
        meter.current = { read: m.read, close() { m.close(); stream.getTracks().forEach((t) => t.stop()); } };
      }).catch(() => {});
    }
    recognition.onresult = (e) => {
      lastWords = Date.now();
      let final = "", interim = "";
      for (let i = 0; i < e.results.length; i += 1) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final = joinText(final, t); else interim = joinText(interim, t);
      }
      heard = final;
      session.written = joinText(session.base, session.done, final, interim);
      liveOut.current(session.written);
    };
    recognition.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        session.active = false;
        toast.error("Autorise le micro pour dicter ton message");
      } else if (e.error === "network" || e.error === "language-not-supported" || e.error === "audio-capture") {
        session.active = false;
        session.fallback = true;
        browserDictationBroken = true;
      }
    };
    recognition.onend = () => {
      session.done = joinText(session.done, heard);
      heard = "";
      if (!session.cancelled) liveOut.current(joinText(session.base, session.done));
      if (session.active) {
        try { recognition.start(); return; } catch { /* relance impossible : on s'arrête */ }
      }
      live.current = null;
      closeMeter();
      setState("idle");
      if (session.fallback && !session.cancelled) startChunked(joinText(session.base, session.done));
    };
    try { recognition.start(); }
    catch { closeMeter(); toast.error("La dictée n'est pas possible dans ce navigateur"); return; }
    live.current = session;
    setSeconds(0);
    setState("recording");
  };

  // Dictée par morceaux (navigateur sans reconnaissance vocale, ou qui la bloque) : le micro
  // enregistre des morceaux de 2 à 7 secondes, coupés dans un silence ; chacun est écrit par
  // le site (Whisper chez Groq, une fraction de seconde) et ajouté à la suite, dans l'ordre.
  // Un morceau sans voix n'est pas envoyé.
  const startChunked = async (base = value || "") => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) { toast.error("La dictée n'est pas possible dans ce navigateur"); return; }
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch { toast.error("Autorise le micro pour dicter ton message"); return; }
    const mime = pickMime();
    const level = streamMeter(stream);
    meter.current = level;
    const session = { stream, active: true, cancelled: false, base, parts: [], pending: 0, done: false };
    const show = () => {
      if (session.cancelled) return;
      session.written = joinText(session.base, ...session.parts.filter(Boolean));
      liveOut.current(session.written);
    };
    session.finish = () => {
      if (session.done || (session.pending > 0 && !session.cancelled) || (session.recorder?.state === "recording" && !session.cancelled)) return;
      session.done = true;
      clearInterval(session.watch);
      if (session.recorder?.state === "recording") { session.recorder.onstop = null; session.recorder.stop(); }
      stream.getTracks().forEach((t) => t.stop());
      if (chunked.current === session) { chunked.current = null; closeMeter(); setState("idle"); }
    };
    const send = async (blob, index) => {
      session.pending += 1;
      try {
        const type = blob.type || "audio/webm";
        const form = new FormData();
        form.append("file", blob, `dictee.${type.includes("mp4") ? "m4a" : type.split("/")[1].split(";")[0]}`);
        form.append("language", lang || "");
        const r = await vai.post("/transcribe", form);
        session.parts[index] = (r.text || "").trim();
        show();
      } catch (err) {
        if (!session.cancelled && !session.warned) { session.warned = true; toast.error(errorText(err, "Transcription impossible")); }
      } finally {
        session.pending -= 1;
        if (!session.active) session.finish();
      }
    };
    const segment = () => {
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      const begun = Date.now();
      let peak = 0, quietSince = 0;
      const index = session.parts.length;
      session.parts.push("");
      recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      recorder.onstop = () => {
        clearInterval(session.watch);
        if (session.active) segment();  // le morceau suivant commence tout de suite
        const blob = new Blob(chunks, { type: (recorder.mimeType || mime || "audio/webm").split(";")[0] });
        if (!session.cancelled && peak > 0.08 && blob.size > 1500) send(blob, index);
        else if (!session.active) session.finish();
      };
      session.watch = setInterval(() => {
        const now = Date.now(), v = level ? level.read() : 0.5;
        peak = Math.max(peak, v);
        quietSince = v < 0.05 ? quietSince || now : 0;
        const elapsed = now - begun;
        if (elapsed > 7000 || (elapsed > 2000 && peak > 0.08 && quietSince && now - quietSince > 350)) {
          if (recorder.state === "recording") recorder.stop();
        }
      }, 100);
      session.recorder = recorder;
      recorder.start();
    };
    chunked.current = session;
    segment();
    setSeconds(0);
    setState("recording");
  };

  const start = () => {
    if (SpeechRecognition && !browserDictationBroken) startLive();
    else startChunked();
  };

  if (state === "recording") {
    return (
      <span className="flex items-center gap-1.5" title={`${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`}>
        <VoiceWave meter={meter} />
        <button type="button" onClick={stop} className="w-8 h-8 rounded-full bg-white/[0.1] flex items-center justify-center hover:bg-white/[0.16]" aria-label="Arrêter la dictée" data-testid="vai-dictate-stop">
          <span className="w-2.5 h-2.5 rounded-[3px] bg-white" />
        </button>
      </span>
    );
  }
  return (
    <button type="button" onClick={start} disabled={disabled} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5 disabled:opacity-40"
      aria-label="Dicter un message" title="Dicter un message" data-testid="vai-dictate">
      <Mic className="w-4 h-4" />
    </button>
  );
}

const FILE_ICONS = { zip: FileArchive, image: ImageIcon, file: FileCode2 };

function AttachmentChip({ att, onRemove }) {
  const Icon = FILE_ICONS[att.kind] || (att.name.endsWith(".zip") ? FileArchive : /\.(png|jpe?g|webp|gif)$/i.test(att.name) ? ImageIcon : FileCode2);
  return (
    <span className={`inline-flex items-center gap-2 max-w-[240px] h-10 pl-2 pr-1 rounded-xl border text-xs ${att.status === "error" ? "border-red-400/30 bg-red-500/10" : "border-white/10 bg-white/[0.04]"}`} data-testid="vai-attachment">
      <span className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
        {att.status === "uploading" ? <Loader2 className="w-3.5 h-3.5 animate-spin text-white/60" /> : <Icon className="w-3.5 h-3.5 text-sky-300" />}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-white/85">{att.name}</span>
        <span className="block truncate text-[10px] text-white/40">{att.status === "uploading" ? (att.kind === "image" ? "Lecture de la photo…" : "Envoi…") : att.status === "error" ? att.error : att.note || { zip: "Projet .zip", image: "Photo", file: "Fichier" }[att.kind]}</span>
      </span>
      <button type="button" onClick={onRemove} className="p-1 rounded hover:bg-white/10 text-white/50 hover:text-white" aria-label={`Retirer ${att.name}`}><X className="w-3 h-3" /></button>
    </span>
  );
}

export default function Composer({
  value, onChange, onSend, onStop, streaming, disabled, disabledReason, me, model, onModel, effort, onEffort, tools, onTools, toolMode, onToolMode, attachments, onAttachments,
  onUpgrade, onArtifacts, autoFocus, placeholder,
}) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const [slashIndex, setSlashIndex] = useState(0);
  const [dragging, setDragging] = useState(false);
  const atts = attachments;
  const setAtts = onAttachments;
  const attsRef = useRef(atts);
  attsRef.current = atts;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
  }, [value]);
  useEffect(() => { if (autoFocus) ref.current?.focus(); }, [autoFocus]);
  // Ctrl+U (⌘U) : ajouter des fichiers, comme sur claude.ai.
  useEffect(() => {
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "u") { e.preventDefault(); fileRef.current?.click(); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const upload = (files) => {
    const list = [...files].slice(0, Math.max(0, 5 - attsRef.current.length));
    if (files.length > list.length) toast.info("5 pièces jointes au plus par message");
    list.forEach(async (file) => {
      const local = `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const kind = /\.zip$/i.test(file.name) ? "zip" : file.type.startsWith("image/") ? "image" : "file";
      setAtts((a) => [...a, { local, name: file.name || "photo.png", kind, status: "uploading" }]);
      try {
        const form = new FormData();
        form.append("file", file, file.name || "photo.png");
        const r = await vai.post("/attachments", form);
        setAtts((a) => a.map((x) => (x.local === local ? { ...x, ...r, status: "ready", note: r.truncated ? "Long : seul le début sera lu" : r.note } : x)));
      } catch (err) {
        setAtts((a) => a.map((x) => (x.local === local ? { ...x, status: "error", error: errorText(err, "Envoi impossible") } : x)));
      }
    });
  };

  const slashMatches = useMemo(() => {
    const m = value.match(/^\/(\S*)$/);
    if (!m) return [];
    return SLASH_COMMANDS.filter((c) => c.cmd.startsWith(m[1].toLowerCase()));
  }, [value]);
  useEffect(() => { setSlashIndex(0); }, [slashMatches.length]);
  const pickSlash = (c) => {
    const tool = c.tool && me.tools.find((t) => t.id === c.tool);
    if (tool && !tool.allowed) { onUpgrade(); return; }
    onChange(c.prompt);
    if (c.tool && toolMode === "manual") onTools([c.tool]);
    setTimeout(() => { const el = ref.current; if (el) { el.focus(); el.setSelectionRange(c.prompt.length, c.prompt.length); } }, 0);
  };

  const uploading = atts.some((a) => a.status === "uploading");
  const ready = atts.filter((a) => a.status === "ready");
  const canSend = (!!value.trim() || ready.length > 0) && !uploading && !disabled && !streaming;
  const select = (id) => onTools(tools.includes(id) ? [] : [id]);  // Manuel : un seul outil à la fois
  const activeChips = toolMode === "manual" ? tools.filter((id) => me.tools.find((t) => t.id === id && t.allowed && t.available)) : [];

  const onKeyDown = (e) => {
    if (slashMatches.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSlashIndex((i) => (i + 1) % slashMatches.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setSlashIndex((i) => (i - 1 + slashMatches.length) % slashMatches.length); return; }
      if ((e.key === "Enter" && !e.shiftKey) || e.key === "Tab") { e.preventDefault(); pickSlash(slashMatches[slashIndex]); return; }
      if (e.key === "Escape") { onChange(""); return; }
    }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); if (canSend) onSend(); }
  };

  return (
    <div className={`relative rounded-[22px] border bg-[#17171f] shadow-[0_10px_40px_rgba(0,0,0,0.35)] transition-colors ${dragging ? "border-sky-400/60" : "border-white/10 focus-within:border-white/20"}`}
      onDragOver={(e) => { if (e.dataTransfer?.types?.includes("Files")) { e.preventDefault(); setDragging(true); } }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { if (e.dataTransfer?.files?.length) { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); } }}
      data-testid="vai-composer">
      <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { upload(e.target.files || []); e.target.value = ""; }} data-testid="vai-file-input" />

      {slashMatches.length > 0 && (
        <div className={`${MENU} bottom-full mb-2 left-0 w-full sm:w-96`} data-testid="vai-slash-menu">
          <p className="px-2.5 pt-0.5 pb-1 text-[11px] uppercase tracking-wide text-white/35">Commandes</p>
          {slashMatches.map((c, i) => {
            const tool = c.tool && me.tools.find((t) => t.id === c.tool);
            return (
              <button key={c.cmd} type="button" onMouseEnter={() => setSlashIndex(i)} onClick={() => pickSlash(c)}
                className={`${ITEM} ${i === slashIndex ? "bg-white/[0.06]" : ""}`} data-testid={`vai-slash-${c.cmd}`}>
                <code className="text-sky-300 text-xs w-16 shrink-0">/{c.cmd}</code>
                <span className="flex-1">{c.label}</span>
                {tool && !tool.allowed && <Lock className="w-3.5 h-3.5 text-white/40" />}
              </button>
            );
          })}
        </div>
      )}

      {atts.length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 pt-3">
          {atts.map((a) => <AttachmentChip key={a.local} att={a} onRemove={() => setAtts((list) => list.filter((x) => x.local !== a.local))} />)}
        </div>
      )}
      <textarea ref={ref} value={value} rows={1} disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={(e) => { const files = [...(e.clipboardData?.files || [])]; if (files.length) { e.preventDefault(); upload(files); } }}
        placeholder={disabled ? disabledReason : placeholder || "Écris ton message… (/ pour les commandes)"}
        className="w-full resize-none bg-transparent px-4 pt-4 pb-1 text-[15px] leading-6 outline-none placeholder:text-white/35 disabled:cursor-not-allowed max-h-80"
        data-testid="vai-input" />
      <div className="flex items-center gap-2 px-3 pb-3 pt-1">
        <PlusMenu me={me} tools={tools} toolMode={toolMode} onToolMode={onToolMode} onSelect={select} onUpgrade={onUpgrade} onFiles={() => fileRef.current?.click()}
          onSlash={() => { onChange("/"); ref.current?.focus(); }} onArtifacts={onArtifacts} />
        <div className="flex flex-wrap gap-1.5 min-w-0">
          {activeChips.map((id) => {
            const Icon = TOOL_ICONS[id];
            const t = me.tools.find((x) => x.id === id);
            return (
              <span key={id} className="inline-flex items-center gap-1 h-7 pl-2 pr-1 whitespace-nowrap rounded-lg bg-sky-500/10 border border-sky-400/20 text-xs text-sky-200">
                <Icon className="w-3.5 h-3.5" />{t.name}
                <button type="button" onClick={() => select(id)} className="p-0.5 rounded hover:bg-white/10" aria-label={`Retirer ${t.name}`}><X className="w-3 h-3" /></button>
              </span>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-1">
          <EffortPicker me={me} model={model} effort={effort} onEffort={onEffort} onModel={onModel} onUpgrade={onUpgrade} />
          <ModelPicker me={me} value={model} onChange={onModel} onUpgrade={onUpgrade} />
          <DictateButton disabled={disabled || streaming} value={value} lang={dictationLang(me)}
            onLive={(t) => { onChange(t); const el = ref.current; if (el) el.scrollTop = el.scrollHeight; }}
            onText={(t) => { onChange(value.trim() ? `${value.trimEnd()} ${t}` : t); ref.current?.focus(); }} />
          {streaming ? (
            <button type="button" onClick={onStop} className="w-8 h-8 rounded-full bg-white text-black flex items-center justify-center hover:bg-white/90" aria-label="Arrêter" data-testid="vai-stop"><Square className="w-3 h-3 fill-current" /></button>
          ) : (
            <button type="button" onClick={onSend} disabled={!canSend} className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:brightness-110" aria-label="Envoyer" data-testid="vai-send"><ArrowUp className="w-4 h-4" /></button>
          )}
        </div>
      </div>
    </div>
  );
}
